import { useState, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useApp } from '@/components/AppLayout';
import { Upload, FileText, CheckCircle2, AlertCircle, AlertTriangle, Copy, Download, Sparkles } from 'lucide-react';
import {
  itemApi,
  type ItemImportPreviewResponse,
  type ItemImportPreviewRow,
  type ItemImportExecuteResponse,
} from '@/lib/api';
import { cn } from '@/lib/utils';
import { UltimateOwlUpsellModal } from '@/components/Modals/UltimateOwlUpsellModal';

interface ItemImportDialogProps {
  open: boolean;
  onClose: () => void;
  businessId: string;
  onImported: () => void;
}

type Step = 'upload' | 'preview' | 'results';

export function ItemImportDialog({ open, onClose, businessId, onImported }: ItemImportDialogProps) {
  const { currentBusiness, billingStatus } = useApp();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Preview state
  const [preview, setPreview] = useState<ItemImportPreviewResponse | null>(null);
  const [duplicateHandling, setDuplicateHandling] = useState<'skip' | 'update' | 'create'>('skip');
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  // Results state
  const [results, setResults] = useState<ItemImportExecuteResponse | null>(null);

  // Upsell modal state
  const [showUpsellModal, setShowUpsellModal] = useState(false);

  const resetState = useCallback(() => {
    setStep('upload');
    setFile(null);
    setDragOver(false);
    setLoading(false);
    setError('');
    setPreview(null);
    setDuplicateHandling('skip');
    setExpandedRows(new Set());
    setResults(null);
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleFileSelect = useCallback((selectedFile: File) => {
    const name = selectedFile.name.toLowerCase();
    if (!name.endsWith('.csv') && !name.endsWith('.xlsx')) {
      setError('Please select a CSV or Excel (.xlsx) file');
      return;
    }
    setFile(selectedFile);
    setError('');
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileSelect(droppedFile);
    }
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handlePreview = useCallback(async () => {
    if (!file) return;

    setLoading(true);
    setError('');

    const response = await itemApi.importPreview(businessId, file);

    setLoading(false);

    if (response.error) {
      setError(response.error);
      return;
    }

    setPreview(response.data!);
    setStep('preview');
  }, [file, businessId]);

  const handleImport = useCallback(async () => {
    if (!file) return;

    setLoading(true);
    setError('');

    const response = await itemApi.importExecute(businessId, file, duplicateHandling);

    setLoading(false);

    if (response.error) {
      setError(response.error);
      return;
    }

    setResults(response.data!);
    setStep('results');
    onImported();
  }, [file, businessId, duplicateHandling, onImported]);

  const toggleRowExpanded = useCallback((rowNumber: number) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(rowNumber)) {
        next.delete(rowNumber);
      } else {
        next.add(rowNumber);
      }
      return next;
    });
  }, []);

  const getStatusBadge = (status: ItemImportPreviewRow['status']) => {
    switch (status) {
      case 'valid':
        return <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">Valid</span>;
      case 'warning':
        return <span className="px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-700">Warning</span>;
      case 'error':
        return <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700">Error</span>;
      case 'duplicate':
        return <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700">Duplicate</span>;
      default:
        return null;
    }
  };

  const importableCount = preview
    ? preview.summary.valid + (duplicateHandling !== 'skip' ? preview.summary.duplicates : 0)
    : 0;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-background flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 flex-shrink-0">
        <div className="flex items-center gap-3">
          <img src="/owl.svg" alt="InvoiceOwl" className="h-8 w-auto" />
          <div className="h-6 w-px bg-border" />
          <span className="font-medium text-sm">{currentBusiness?.name}</span>
        </div>
      </div>

      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto">
        <div className="min-h-full flex flex-col items-center justify-center px-4 py-16">
          <div className="w-full max-w-2xl">
            {step === 'upload' && (
              <>
                <h1 className="text-2xl mb-8 flex items-center gap-2">
                  Import Items
                  <Upload className="h-5 w-5 text-blue-400" />
                </h1>

                {/* Drop zone */}
                <div
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onClick={() => fileInputRef.current?.click()}
                  className={cn(
                    "border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors",
                    dragOver ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-primary/50",
                    file && "border-green-500 bg-green-50"
                  )}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx"
                    onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                    className="hidden"
                  />

                  {file ? (
                    <div className="flex flex-col items-center gap-2">
                      <FileText className="h-12 w-12 text-green-600" />
                      <p className="font-medium">{file.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {(file.size / 1024).toFixed(1)} KB
                      </p>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFile(null);
                        }}
                        className="text-sm text-primary hover:underline mt-2"
                      >
                        Choose different file
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Upload className="h-12 w-12 text-muted-foreground" />
                      <p className="font-medium">Drop your file here</p>
                      <p className="text-sm text-muted-foreground">CSV or Excel (.xlsx)</p>
                    </div>
                  )}
                </div>

                {/* Download template link */}
                <div className="mt-4 text-center">
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await itemApi.downloadImportTemplate(businessId);
                      } catch {
                        setError('Failed to download template');
                      }
                    }}
                    className="text-sm text-primary hover:underline inline-flex items-center gap-1"
                  >
                    <Download className="h-4 w-4" />
                    Download CSV template
                  </button>
                </div>

                {/* Column explanation - spreadsheet style */}
                <div className="mt-6">
                  <h3 className="font-medium mb-3 text-sm">Expected Columns</h3>
                  <div className="overflow-x-auto">
                    <table className="text-sm border-collapse border border-border">
                      <thead>
                        <tr>
                          <th className="bg-muted px-3 py-2 border border-border font-medium">
                            Name<span className="text-destructive">*</span>
                          </th>
                          <th className="bg-muted px-3 py-2 border border-border font-medium">Code</th>
                          <th className="bg-muted px-3 py-2 border border-border font-medium">Description</th>
                          <th className="bg-muted px-3 py-2 border border-border font-medium">
                            Rate<span className="text-destructive">*</span>
                          </th>
                          <th className="bg-muted px-3 py-2 border border-border font-medium">Quantity</th>
                          <th className="bg-muted px-3 py-2 border border-border font-medium">Type</th>
                          <th className="bg-muted px-3 py-2 border border-border font-medium">Unit</th>
                        </tr>
                      </thead>
                    </table>
                  </div>
                  <p className="text-sm text-muted-foreground mt-3">
                    <span className="text-destructive">*</span> Name and Rate are required. All other columns are optional.<br />
                    Type: "parts" or "labor" (defaults to parts)
                  </p>
                </div>

                {/* White Glove Import Upsell - only for non-ultimate users */}
                {billingStatus?.plan !== 'unlimited' && (
                  <div className="mt-8 rounded-lg bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-100 p-4">
                    <div className="flex items-start gap-3">
                      <Sparkles className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-amber-900">
                          <span className="font-medium">Need help with a complex import?</span>
                          {' '}Ultimate Owl includes white glove data migration — we'll handle your spreadsheets for you.
                        </p>
                        <button
                          type="button"
                          onClick={() => setShowUpsellModal(true)}
                          className="text-sm text-amber-700 hover:text-amber-800 hover:underline mt-1 inline-block"
                        >
                          Learn about Ultimate Owl
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <UltimateOwlUpsellModal
                  open={showUpsellModal}
                  onClose={() => setShowUpsellModal(false)}
                  businessId={businessId}
                />

                {error && (
                  <p className="text-sm text-destructive mt-4 text-center">{error}</p>
                )}

                <div className="flex gap-3 mt-8">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleClose}
                    className="flex-1 rounded-full"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handlePreview}
                    className="flex-1 rounded-full"
                    disabled={!file || loading}
                  >
                    {loading ? 'Processing...' : 'Continue'}
                  </Button>
                </div>
              </>
            )}

            {step === 'preview' && preview && (
              <>
                <h1 className="text-2xl mb-6">Review Import</h1>

                {/* Summary cards */}
                <div className="grid grid-cols-4 gap-3 mb-6">
                  <div className="bg-muted rounded-lg p-4 text-center">
                    <div className="text-2xl font-semibold">{preview.summary.total}</div>
                    <div className="text-sm text-muted-foreground">Total</div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-semibold text-green-700">{preview.summary.valid}</div>
                    <div className="text-sm text-green-600">Valid</div>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-semibold text-yellow-700">{preview.summary.warnings}</div>
                    <div className="text-sm text-yellow-600">Warnings</div>
                  </div>
                  <div className="bg-red-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-semibold text-red-700">{preview.summary.errors}</div>
                    <div className="text-sm text-red-600">Errors</div>
                  </div>
                </div>

                {/* Duplicate handling */}
                {preview.summary.duplicates > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Copy className="h-4 w-4 text-blue-600" />
                      <span className="font-medium text-blue-800">
                        {preview.summary.duplicates} duplicate{preview.summary.duplicates > 1 ? 's' : ''} found
                      </span>
                    </div>
                    <RadioGroup
                      value={duplicateHandling}
                      onValueChange={(value) => setDuplicateHandling(value as typeof duplicateHandling)}
                      className="space-y-2"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="skip" id="skip" />
                        <Label htmlFor="skip" className="font-normal cursor-pointer">
                          Skip duplicates (don't import)
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="update" id="update" />
                        <Label htmlFor="update" className="font-normal cursor-pointer">
                          Update existing items with new data
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="create" id="create" />
                        <Label htmlFor="create" className="font-normal cursor-pointer">
                          Create as new items anyway
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>
                )}

                {/* Preview table */}
                <div className="border rounded-lg overflow-hidden mb-6">
                  <div className="max-h-80 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted sticky top-0">
                        <tr>
                          <th className="text-left p-3 font-medium">Row</th>
                          <th className="text-left p-3 font-medium">Name</th>
                          <th className="text-left p-3 font-medium">Code</th>
                          <th className="text-right p-3 font-medium">Rate</th>
                          <th className="text-left p-3 font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.rows.map((row) => (
                          <>
                            <tr
                              key={row.row_number}
                              onClick={() => (row.errors.length > 0 || row.warnings.length > 0) && toggleRowExpanded(row.row_number)}
                              className={cn(
                                "border-t",
                                (row.errors.length > 0 || row.warnings.length > 0) && "cursor-pointer hover:bg-muted/50"
                              )}
                            >
                              <td className="p-3 text-muted-foreground">{row.row_number}</td>
                              <td className="p-3">{row.data.name || <span className="text-muted-foreground italic">Missing</span>}</td>
                              <td className="p-3 text-muted-foreground">{row.data.code || '-'}</td>
                              <td className="p-3 text-right">${row.data.rate}</td>
                              <td className="p-3">{getStatusBadge(row.status)}</td>
                            </tr>
                            {expandedRows.has(row.row_number) && (row.errors.length > 0 || row.warnings.length > 0) && (
                              <tr key={`${row.row_number}-details`} className="bg-muted/30">
                                <td colSpan={5} className="p-3 pl-10">
                                  {row.errors.map((err, i) => (
                                    <div key={i} className="flex items-center gap-2 text-red-600">
                                      <AlertCircle className="h-3 w-3" />
                                      {err}
                                    </div>
                                  ))}
                                  {row.warnings.map((warn, i) => (
                                    <div key={i} className="flex items-center gap-2 text-yellow-600">
                                      <AlertTriangle className="h-3 w-3" />
                                      {warn}
                                    </div>
                                  ))}
                                </td>
                              </tr>
                            )}
                          </>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {error && (
                  <p className="text-sm text-destructive mb-4">{error}</p>
                )}

                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setStep('upload');
                      setPreview(null);
                      setError('');
                    }}
                    className="flex-1 rounded-full"
                  >
                    Go Back
                  </Button>
                  <Button
                    onClick={handleImport}
                    className="flex-1 rounded-full"
                    disabled={loading || importableCount === 0}
                  >
                    {loading ? 'Importing...' : `Import ${importableCount} Item${importableCount !== 1 ? 's' : ''}`}
                  </Button>
                </div>
              </>
            )}

            {step === 'results' && results && (
              <>
                <div className="text-center mb-8">
                  <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
                  <h1 className="text-2xl mb-2">Import Complete</h1>
                </div>

                {/* Results summary */}
                <div className="bg-muted rounded-lg p-6 mb-6">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-3xl font-semibold text-green-600">{results.created}</div>
                      <div className="text-sm text-muted-foreground">Created</div>
                    </div>
                    <div>
                      <div className="text-3xl font-semibold text-blue-600">{results.updated}</div>
                      <div className="text-sm text-muted-foreground">Updated</div>
                    </div>
                    <div>
                      <div className="text-3xl font-semibold text-muted-foreground">{results.skipped}</div>
                      <div className="text-sm text-muted-foreground">Skipped</div>
                    </div>
                  </div>
                </div>

                {/* Error list if any */}
                {results.errors.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="h-4 w-4 text-red-600" />
                      <span className="font-medium text-red-800">
                        {results.errors.length} row{results.errors.length > 1 ? 's' : ''} had errors
                      </span>
                    </div>
                    <ul className="text-sm text-red-700 space-y-1">
                      {results.errors.slice(0, 10).map((err, i) => (
                        <li key={i}>
                          Row {err.row}: {err.errors.join(', ')}
                        </li>
                      ))}
                      {results.errors.length > 10 && (
                        <li className="text-muted-foreground">
                          ...and {results.errors.length - 10} more
                        </li>
                      )}
                    </ul>
                  </div>
                )}

                <Button
                  onClick={handleClose}
                  className="w-full rounded-full"
                >
                  Done
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
